/**
 * HTTP Client with Enhanced Reliability
 *
 * Features:
 * - Token caching to reduce network overhead
 * - Exponential backoff retry for transient failures
 * - Circuit breaker pattern for failing services
 * - Guest checkout support (no redirect on public paths)
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 */

import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import configuration from "@/config/configuration";
import { logger } from "@/lib/logger";
import { tokenCache, fetchSession } from "@/lib/token-cache";
import { redirectToAuthLogin } from "@/lib/auth-redirect";

// Re-export fetchSession for backward compatibility
export { fetchSession };

// ============================================================================
// Public Path Detection (Guest Checkout Support)
// ============================================================================

/**
 * Public paths that should NOT trigger login redirect on 401
 * These paths support guest checkout flow
 */
const PUBLIC_PATHS = [
  "/cart",
  "/add-to-cart",
  "/Basket",
  "/checkout",
  "/return",
  "/thanks",
  "/list",
  "/offline",
  "/address",
  // Unsubscribe pages — loaded from email links by unauthenticated users.
  // Must not redirect to login on any API error.
  "/unsubscribe-cart",
  "/migration/unsubscribe",
  "/trial/unsubscribed",
  // Trial registration pages — publicly accessible from WebView
  "/trial/register",
  "/trial/verify",
  "/trial/status",
];

/**
 * Check if current path is a public path that supports guest access
 */
const isPublicPath = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_PATHS.some(
    (publicPath) =>
      pathname.includes(publicPath) ||
      pathname.endsWith(publicPath) ||
      // Handle locale prefix (e.g., /en/cart)
      pathname.match(new RegExp(`^/[a-z]{2}${publicPath}($|/|\\?)`)),
  );
};

// ============================================================================
// Circuit Breaker
// ============================================================================

interface CircuitState {
  failures: number;
  lastFailure: number;
  isOpen: boolean;
}

const circuitBreakers = new Map<string, CircuitState>();
const CIRCUIT_FAILURE_THRESHOLD = 5;
const CIRCUIT_RESET_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Get or create circuit state for a service
 */
const getCircuitState = (service: string): CircuitState => {
  if (!circuitBreakers.has(service)) {
    circuitBreakers.set(service, {
      failures: 0,
      lastFailure: 0,
      isOpen: false,
    });
  }
  return circuitBreakers.get(service)!;
};

/**
 * Check if circuit is open (failing)
 */
const isCircuitOpen = (service: string): boolean => {
  const state = getCircuitState(service);

  if (!state.isOpen) return false;

  // Check if reset timeout has passed
  if (Date.now() - state.lastFailure > CIRCUIT_RESET_TIMEOUT_MS) {
    // Half-open: allow one request through
    state.isOpen = false;
    state.failures = 0;
    return false;
  }

  return true;
};

/**
 * Record a failure for circuit breaker
 */
const recordFailure = (service: string): void => {
  const state = getCircuitState(service);
  state.failures++;
  state.lastFailure = Date.now();

  if (state.failures >= CIRCUIT_FAILURE_THRESHOLD) {
    state.isOpen = true;
    logger.warn(`Circuit breaker opened for service: ${service}`);
  }
};

/**
 * Record a success (reset failures)
 */
const recordSuccess = (service: string): void => {
  const state = getCircuitState(service);
  state.failures = 0;
  state.isOpen = false;
};

// ============================================================================
// Retry Logic
// ============================================================================

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

/**
 * Check if error is retryable
 */
const isRetryableError = (error: AxiosError): boolean => {
  // Network errors
  if (!error.response) return true;

  // 5xx server errors (except 501 Not Implemented)
  const status = error.response.status;
  if (status >= 500 && status !== 501) return true;

  // 429 Too Many Requests (rate limited)
  if (status === 429) return true;

  return false;
};

/**
 * Calculate retry delay with exponential backoff and jitter
 */
const getRetryDelay = (attempt: number): number => {
  const exponentialDelay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return exponentialDelay + jitter;
};

// ============================================================================
// Axios Instance
// ============================================================================

export const api = axios.create({
  baseURL: configuration.backend_url + "/api/v1",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 30000, // 30 second timeout
});

// ============================================================================
// Request Interceptor
// ============================================================================

api.interceptors.request.use(
  async (config) => {
    // Check circuit breaker
    const service = config.baseURL
      ? new URL(config.baseURL).hostname
      : "unknown";
    if (isCircuitOpen(service)) {
      logger.warn(`Circuit breaker is open for ${service}, rejecting request`);
      return Promise.reject(
        new Error(`Service temporarily unavailable: ${service}`),
      );
    }

    // Add authentication token (uses cached token)
    const token = await tokenCache.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logger.warn("No authentication token available");
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ============================================================================
// Response Interceptor
// ============================================================================

api.interceptors.response.use(
  (response) => {
    // Record success for circuit breaker
    const service = response.config.baseURL
      ? new URL(response.config.baseURL).hostname
      : "unknown";
    recordSuccess(service);

    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    if (!config) {
      return Promise.reject(error);
    }

    const service = config.baseURL
      ? new URL(config.baseURL).hostname
      : "unknown";

    // Handle 401 Unauthorized - token refresh
    if (error.response?.status === 401 && !config._retry) {
      config._retry = true;

      // On public paths (cart, checkout, etc.), don't redirect - allow guest checkout
      if (isPublicPath()) {
        logger.debug(
          "401 on public path - allowing guest access without redirect",
        );
        return Promise.reject(error);
      }

      logger.warn("401 Unauthorized - attempting token refresh");

      // Force token refresh
      tokenCache.clearCache();
      const token = await tokenCache.refreshToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return api(config);
      } else {
        // No valid token -- redirect to login (only for protected routes).
        // Issue 1 (FCR Pass 10): use the shared helper so token is cleared
        // before redirect and the same-origin guard prevents open-redirect.
        logger.error("Authentication failed - redirecting to login");
        redirectToAuthLogin(window.location.href);
        return Promise.reject(error);
      }
    }

    // Retry logic for transient errors
    if (isRetryableError(error)) {
      const retryCount = config._retryCount || 0;

      if (retryCount < MAX_RETRIES) {
        config._retryCount = retryCount + 1;

        const delay = getRetryDelay(retryCount);
        logger.warn(
          `Request failed, retrying (${retryCount + 1}/${MAX_RETRIES}) in ${delay}ms`,
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }

      // All retries exhausted, record failure for circuit breaker
      recordFailure(service);
    }

    return Promise.reject(error);
  },
);


